import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Aspen's typography scale (`heading-lg`, `body-sm`, `caption`, `label-sm`, etc. — see
 * the `addUtilities` typography plugin in tailwind.preset.ts) is a set of compound
 * classes, each setting font-family/font-size/font-weight/line-height/letter-spacing
 * together. They don't follow Tailwind's own `text-*` naming, so tailwind-merge has no
 * way to know they conflict with `text-{xs,sm,base,lg,...}` font-size utilities.
 * Several primitives hardcode one of these (`CardTitle` → `heading-lg`, `CardDescription`
 * → `caption`, `DialogTitle` → `heading-lg`, `Badge` → `label-sm`/`label-md`) and let
 * callers pass a `className` on top — e.g. `<CardTitle className="text-sm">` across the
 * native-data-explorer widget cards, sizing a title down from Figma's default. Without
 * this, `cn('heading-lg', 'text-sm')` keeps BOTH classes (tailwind-merge sees no
 * conflict), and since `heading-lg` is a plugin utility registered after Tailwind's own
 * core `text-*` utilities, it always wins the cascade regardless of `cn()` argument
 * order — silently making every one of those `text-sm` overrides a no-op. List the
 * typography classes here so a later `text-*` (or an earlier one) actually wins.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      shadow: ['shadow-elevation-1'],
      'font-size': [
        'heading-display',
        'heading-lg',
        'heading-md',
        'heading-sm',
        'subtitle',
        'body-lg',
        'body-md-strong',
        'body-md',
        'body-sm',
        'label-md',
        'label-sm',
        'caption',
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Prefixes each Tailwind class with a selector for targeting nested elements.
 *
 * @example
 * // Target menuitem children on hover
 * withSelector('[&_[role=menuitem]]:hover', 'bg-blue-500 text-white')
 * // Returns: '[&_[role=menuitem]]:hover:bg-blue-500 [&_[role=menuitem]]:hover:text-white'
 */
export function withSelector(selector: string, styles: string): string {
  return styles
    .split(/\s+/)
    .filter(Boolean)
    .map((style) => `${selector}:${style}`)
    .join(' ');
}
